import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
} from "@mui/material"
import {
  TrendingUp as TrendingUpIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  TrackChanges as TrackingIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material"
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"
import { apiService, DashboardData } from "../services/apiService"

const Analytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState("7d")

  useEffect(() => {
    fetchAnalyticsData()
  }, [timeRange])

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true)
      const data = await apiService.getDashboardData()
      setAnalyticsData(data)
    } catch (error) {
      console.error("Failed to fetch analytics data:", error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LinearProgress />
  }

  if (!analyticsData) {
    return (
      <Typography variant="h6" color="error">
        Failed to load analytics data
      </Typography>
    )
  }

  // Mock data for charts - in real app, this would come from the API
  const performanceData = [
    { name: "Mon", deliveries: 45, onTime: 42, exceptions: 3 },
    { name: "Tue", deliveries: 52, onTime: 48, exceptions: 4 },
    { name: "Wed", deliveries: 38, onTime: 36, exceptions: 2 },
    { name: "Thu", deliveries: 61, onTime: 58, exceptions: 3 },
    { name: "Fri", deliveries: 55, onTime: 52, exceptions: 3 },
    { name: "Sat", deliveries: 28, onTime: 26, exceptions: 2 },
    { name: "Sun", deliveries: 22, onTime: 21, exceptions: 1 },
  ]

  const costAnalysisData = [
    { name: "Transportation", value: 45, color: "#1976d2" },
    { name: "Warehousing", value: 25, color: "#2e7d32" },
    { name: "Labor", value: 20, color: "#ed6c02" },
    { name: "Technology", value: 10, color: "#9c27b0" },
  ]

  const carrierPerformanceData = [
    { name: "Midwest Transport", shipments: 45, onTimeRate: 96, avgCost: 1250 },
    { name: "Express Logistics", shipments: 32, onTimeRate: 91, avgCost: 1100 },
    { name: "SafeTransport Inc", shipments: 18, onTimeRate: 98, avgCost: 1400 },
    { name: "Global Freight", shipments: 12, onTimeRate: 89, avgCost: 1000 },
  ]

  // Shipment Status Distribution Data
  const shipmentStatusData = [
    {
      name: "In Transit",
      value: analyticsData.tracking.summary.inTransit,
      color: "#1976d2",
      percentage: (
        (analyticsData.tracking.summary.inTransit /
          analyticsData.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "Out for Delivery",
      value: analyticsData.tracking.summary.outForDelivery,
      color: "#ed6c02",
      percentage: (
        (analyticsData.tracking.summary.outForDelivery /
          analyticsData.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "At Facility",
      value: analyticsData.tracking.summary.atFacility,
      color: "#2e7d32",
      percentage: (
        (analyticsData.tracking.summary.atFacility /
          analyticsData.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "Delivered Today",
      value: analyticsData.tracking.summary.deliveredToday,
      color: "#388e3c",
      percentage: (
        (analyticsData.tracking.summary.deliveredToday /
          analyticsData.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
    {
      name: "Exceptions",
      value: analyticsData.tracking.summary.exceptionsToday,
      color: "#d32f2f",
      percentage: (
        (analyticsData.tracking.summary.exceptionsToday /
          analyticsData.tracking.summary.totalActiveShipments) *
        100
      ).toFixed(1),
    },
  ]

  const kpiData = [
    {
      title: "On-Time Delivery Rate",
      value: `${(analyticsData.shipments.onTimeDeliveryRate * 100).toFixed(1)}%`,
      target: "95%",
      status:
        analyticsData.shipments.onTimeDeliveryRate >= 0.95
          ? "success"
          : "warning",
      icon: <CheckCircleIcon />,
    },
    {
      title: "Average Transit Time",
      value: `${analyticsData.shipments.averageTransitTime} days`,
      target: "3 days",
      status:
        analyticsData.shipments.averageTransitTime <= 3 ? "success" : "warning",
      icon: <ScheduleIcon />,
    },
    {
      title: "Exception Rate",
      value: `${((analyticsData.tracking.summary.exceptionsToday / analyticsData.tracking.summary.totalActiveShipments) * 100).toFixed(1)}%`,
      target: "< 5%",
      status:
        analyticsData.tracking.summary.exceptionsToday /
          analyticsData.tracking.summary.totalActiveShipments <=
        0.05
          ? "success"
          : "error",
      icon: <WarningIcon />,
    },
    {
      title: "Inventory Utilization",
      value: `${(analyticsData.inventory.summary.averageUtilization * 100).toFixed(1)}%`,
      target: "80%",
      status:
        analyticsData.inventory.summary.averageUtilization >= 0.8
          ? "success"
          : "warning",
      icon: <InventoryIcon />,
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Analytics & Performance
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            Advanced insights and performance metrics
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {kpiData.map((kpi, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {kpi.title}
                    </Typography>
                    <Typography variant="h4" color={`${kpi.status}.main`}>
                      {kpi.value}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Target: {kpi.target}
                    </Typography>
                  </Box>
                  <Box color={`${kpi.status}.main`}>{kpi.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Performance Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Performance Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="deliveries"
                    stackId="1"
                    stroke="#1976d2"
                    fill="#1976d2"
                    fillOpacity={0.6}
                  />
                  <Area
                    type="monotone"
                    dataKey="onTime"
                    stackId="1"
                    stroke="#2e7d32"
                    fill="#2e7d32"
                    fillOpacity={0.6}
                  />
                  <Area
                    type="monotone"
                    dataKey="exceptions"
                    stackId="1"
                    stroke="#d32f2f"
                    fill="#d32f2f"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={costAnalysisData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {costAnalysisData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Shipment Status Distribution */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Shipment Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={shipmentStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {shipmentStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Breakdown Details
              </Typography>
              <List>
                {shipmentStatusData.map((status, index) => (
                  <React.Fragment key={status.name}>
                    <ListItem>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: status.color,
                            borderRadius: "50%",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={status.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {status.value} shipments ({status.percentage}%)
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              <Chip
                                label={`${status.percentage}%`}
                                color={
                                  status.name === "Exceptions"
                                    ? "error"
                                    : status.name === "Delivered Today"
                                      ? "success"
                                      : "primary"
                                }
                                size="small"
                              />
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {status.value} total
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < shipmentStatusData.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Carrier Performance */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Carrier Performance Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={carrierPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="shipments"
                    fill="#1976d2"
                    name="Shipments"
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="onTimeRate"
                    fill="#2e7d32"
                    name="On-Time Rate %"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Performing Carriers
              </Typography>
              <List>
                {carrierPerformanceData
                  .sort((a, b) => b.onTimeRate - a.onTimeRate)
                  .map((carrier, index) => (
                    <React.Fragment key={carrier.name}>
                      <ListItem>
                        <ListItemIcon>
                          <ShippingIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={carrier.name}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {carrier.shipments} shipments • $
                                {carrier.avgCost} avg cost
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mt: 0.5,
                                }}
                              >
                                <Chip
                                  label={`${carrier.onTimeRate}% on-time`}
                                  color={
                                    carrier.onTimeRate >= 95
                                      ? "success"
                                      : carrier.onTimeRate >= 90
                                        ? "warning"
                                        : "error"
                                  }
                                  size="small"
                                />
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  #{index + 1}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < carrierPerformanceData.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Performance */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Health Metrics
              </Typography>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">API Gateway Uptime</Typography>
                  <Typography variant="body2" color="success.main">
                    99.9%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.9}
                  color="success"
                />
              </Box>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">
                    Shipment Service Uptime
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    99.8%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.8}
                  color="success"
                />
              </Box>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">
                    Inventory Service Uptime
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    99.9%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.9}
                  color="success"
                />
              </Box>
              <Box mb={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">
                    Tracking Service Uptime
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    99.7%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={99.7}
                  color="success"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Key Performance Indicators
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Customer Satisfaction"
                    secondary="4.8/5.0 (96% positive feedback)"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Revenue Growth"
                    secondary="+23% compared to last quarter"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <InventoryIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Inventory Turnover"
                    secondary="8.2x per year (industry avg: 6.5x)"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Average Processing Time"
                    secondary="2.3 hours (target: < 3 hours)"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Analytics
